import { useCallback, useState, useEffect } from "react";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { db } from "../firebaseConfig";

/** Capitaliza: "VICTOR" -> "Victor" */
const cap = (s = "") =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

export function formatShortName(u) {
  if (typeof u === "string") {
    const parts = u.trim().split(/\s+/);
    const first = cap(parts[0] || "");
    const last = cap(parts[1] || "");
    return [first, last].filter(Boolean).join(" ") || "Usuario";
  }

  const firstRaw = (u?.nombre || u?.firstName || u?.displayName || "")
    .toString()
    .trim();
  const lastRaw = (u?.apellido || u?.lastName || "").toString().trim();

  let first = firstRaw.split(/\s+/)[0] || "";
  let last = lastRaw.split(/\s+/)[0] || "";

  if (!first && !last && u?.displayName) {
    const parts = u.displayName.trim().split(/\s+/);
    first = parts[0] || "";
    last = parts[1] || "";
  }

  return (
    [cap(first), cap(last)].filter(Boolean).join(" ") || u?.email || "Usuario"
  );
}

export function useUserAvatar(userId) {
  const [url, setUrl] = useState("/img/avatar2.jpg");

  useEffect(() => {
    if (!userId) return;

    const userDocRef = doc(db, "users", userId);
    const unsub = onSnapshot(userDocRef, (snap) => {
      setUrl(
        snap.exists()
          ? snap.data().avatarUrl || "/img/avatar2.jpg"
          : "/img/avatar2.jpg",
      );
    });

    return () => unsub();
  }, [userId]);

  return url;
}

export function useAvatarUpload({ user, setAvatarUrl, persistAvatarUrl }) {
  const [uploading, setUploading] = useState(false);

  const uploadAvatar = useCallback(
    async (file) => {
      if (!user?.uid || !file) return null;

      setUploading(true);
      try {
        const storage = getStorage();
        const path = `avatars/${user.uid}/${Date.now()}-${file.name}`;
        const ref = storageRef(storage, path);

        await uploadBytes(ref, file);
        const photoURL = await getDownloadURL(ref);

        // ✅ 1) UI inmediata
        setAvatarUrl?.(photoURL);

        // ✅ 2) persistir en Firestore (sin bloquear la UI)
        if (persistAvatarUrl) {
          await persistAvatarUrl(photoURL);
        } else {
          await setDoc(
            doc(db, "users", user.uid),
            { avatarUrl: photoURL },
            { merge: true },
          );
        }

        // ✅ 3) Auth
        await updateProfile(user, { photoURL });

        return photoURL;
      } finally {
        setUploading(false);
      }
    },
    [user, setAvatarUrl, persistAvatarUrl],
  );

  return { uploadAvatar, uploading };
}
